import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Slider,
  Paper,
  IconButton,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const Settings = ({ profiles, selectedProfileIndex, onUpdateProfiles }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Normalize existing profiles to ensure default values for width and height
  const normalizeProfiles = (profiles) => {
    return profiles.map((profile) => ({
      ...profile,
      channels: profile.channels.map((channel) => ({
        ...channel,
        width: channel.width ?? 300, // Default to 300
        height: channel.height ?? 1, // Default to 1
      })),
    }));
  };

  const normalizedProfiles = normalizeProfiles(profiles);
  const selectedProfile = normalizedProfiles[selectedProfileIndex];

  const handleAddProfile = () => {
    const newProfile = {
      name: `Profile ${profiles.length + 1}`,
      channels: Array.from({ length: 6 }, (_, i) => ({
        name: i === 5 ? 'ALL LEDS' : `Channel ${i}`,
        ledCount: i === 5 ? 300 : 150,
        width: 300, // Default width
        height: 1, // Default height
      })),
    };
    const updatedProfiles = [...normalizedProfiles, newProfile];
    onUpdateProfiles(updatedProfiles, updatedProfiles.length - 1);
  };

  const handleDeleteProfile = () => {
    if (profiles.length > 1) {
      const updatedProfiles = normalizedProfiles.filter(
        (_, i) => i !== selectedProfileIndex
      );
      onUpdateProfiles(updatedProfiles, Math.max(0, selectedProfileIndex - 1));
      setShowDeleteConfirm(false);
    }
  };

  const handleProfileNameChange = (name) => {
    const updatedProfiles = [...normalizedProfiles];
    updatedProfiles[selectedProfileIndex].name = name;
    onUpdateProfiles(updatedProfiles, selectedProfileIndex);
  };

  const handleChannelChange = (channelIndex, key, value) => {
    const updatedProfiles = [...normalizedProfiles];
    updatedProfiles[selectedProfileIndex].channels[channelIndex][key] = value;
    onUpdateProfiles(updatedProfiles, selectedProfileIndex);
  };

  const handleProfileSelection = (index) => {
    onUpdateProfiles(normalizedProfiles, index);
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h5" align="center" gutterBottom>
        Settings: Profiles
      </Typography>

      {/* Profile Selection */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}
      >
        <Select
          value={selectedProfileIndex}
          onChange={(e) => handleProfileSelection(e.target.value)}
          fullWidth
          size="small"
        >
          {normalizedProfiles.map((profile, index) => (
            <MenuItem key={index} value={index}>
              {profile.name}
            </MenuItem>
          ))}
        </Select>
        <Button variant="contained" onClick={handleAddProfile}>
          Add Profile
        </Button>
        {normalizedProfiles.length > 1 && (
          <IconButton color="error" onClick={() => setShowDeleteConfirm(true)}>
            <DeleteIcon />
          </IconButton>
        )}
      </Box>

      {/* Confirmation Dialog for Deleting a Profile */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the profile "{selectedProfile?.name}
            "? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteProfile} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {selectedProfile && (
        <Paper
          sx={{
            padding: 2,
            marginBottom: 2,
            backgroundColor: '#f9f9f9',
            borderRadius: 2,
          }}
        >
          {/* Profile Name */}
          <Box sx={{ marginBottom: 2 }}>
            <TextField
              label="Profile Name"
              value={selectedProfile.name}
              onChange={(e) => handleProfileNameChange(e.target.value)}
              fullWidth
              size="small"
            />
          </Box>

          {/* LED Channels */}
          {selectedProfile.channels.map((channel, index) => (
            <Paper
              key={index}
              sx={{
                padding: 2,
                marginBottom: 1,
                backgroundColor: index === 5 ? '#f0f0f0' : '#ffffff',
                pointerEvents: index === 5 ? 'none' : 'auto',
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 1,
                }}
              >
                <Typography variant="h6" sx={{ marginRight: 1 }}>
                  {channel.name}
                </Typography>
                {index !== 5 && (
                  <TextField
                    value={channel.name}
                    onChange={(e) =>
                      handleChannelChange(index, 'name', e.target.value)
                    }
                    size="small"
                    sx={{ flex: 1, marginLeft: 1 }}
                  />
                )}
              </Box>

              {/* LED Count */}
              <Typography variant="body2">
                LED Count: {channel.ledCount}
              </Typography>
              <Slider
                value={channel.ledCount}
                onChange={(_, value) =>
                  handleChannelChange(index, 'ledCount', value)
                }
                min={1}
                max={300}
                step={1}
                disabled={index === 5}
              />

              {/* Width and Height */}
              <Box sx={{ display: 'flex', gap: 2, marginTop: 2 }}>
                <TextField
                  label="Width"
                  type="number"
                  value={channel.width}
                  onChange={(e) =>
                    handleChannelChange(index, 'width', +e.target.value)
                  }
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Height"
                  type="number"
                  value={channel.height}
                  onChange={(e) =>
                    handleChannelChange(index, 'height', +e.target.value)
                  }
                  size="small"
                  fullWidth
                />
              </Box>
            </Paper>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default Settings;
